import { fabric } from 'fabric';
import { TextOptions } from 'fabric/fabric-impl';
import { uniqueId } from 'lodash';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  INCH_QUOTE,
  KEYS,
  MEASUREMENT_LABEL_MAX_LENGTH,
  TEN_THOUSAND,
} from '~/core/constants';
import { DrawingShape, MeasureInfo } from '~/core/models';
import theme from '~/theme/theme';

// Fix for textBaseline error - override fabric.js behavior
const originalTextBaseline = Object.getOwnPropertyDescriptor(
  CanvasRenderingContext2D.prototype,
  'textBaseline'
);

if (originalTextBaseline) {
  Object.defineProperty(CanvasRenderingContext2D.prototype, 'textBaseline', {
    get: originalTextBaseline.get,
    set: function (value: string) {
      // Fix the incorrect 'alphabetical' value
      if (value === 'alphabetical') {
        value = 'alphabetic';
      }
      originalTextBaseline.set?.call(this, value);
    },
  });
}

const {
  drawingTool: {
    fontFamily,
    drawingShape: { stroke },
  },
} = theme.light;

export class EditableLabel {
  parentShape: DrawingShape;
  labelText: fabric.Text;
  measurementText: fabric.IText;
  canvas: fabric.Canvas;

  constructor(
    parentShape: DrawingShape,
    canvas: fabric.Canvas,
    interiorColor: string,
    label: string,
    Info: MeasureInfo
  ) {
    // Determine if the label is an arrow label (i.e. a single uppercase letter)
    const isArrowLabel = /^[A-Z]$/.test(label);
    // Use a different prefix based on whether it’s an arrow label
    const prefix = isArrowLabel
      ? CANVAS_OBJECT_TYPE_PREFIX.ARROW_LABEL
      : CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL;

    this.parentShape = parentShape;
    this.canvas = canvas;
    this.labelText = new fabric.Text(`${label}`, {
      stroke: stroke.normal,
      fill: stroke.normal,
      strokeWidth: 0.1,
      fontSize: 3,
      fontFamily,
      name: `${prefix}_${uniqueId()}`,
      hoverCursor: 'pointer',
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      selectable: false,
      data: {
        highlightable: true,
        inInterior: true,
      },
    });
    this.measurementText = new fabric.IText(`${Info.size}${INCH_QUOTE}`, {
      fontFamily,
      fill: interiorColor,
      stroke: stroke.normal,
      strokeWidth: 0.1,
      editable: true,
      fontSize: 3,
      selectable: true,
      hoverCursor: 'pointer',
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL}_${uniqueId()}`,
      data: {
        highlightable: true,
        inInterior: true,
      },
    });
    this.measurementText.on('mousedown', () => {
      if (this.measurementText.name) {
        // ignore orphaned labels
        const hasEditableLabels = this.parentShape.editableLabels.some(
          (editableLabel) =>
            editableLabel.measurementText.name === this.measurementText.name
        );

        if (hasEditableLabels) {
          this.measurementText.enterEditing();
        } else {
          // TODO orphaned label not removable, so moving way off to right as a workaround
          // Its no longer an object on the canvas yet it shows until next refresh
          this.measurementText.left = TEN_THOUSAND;
        }
      }
    });

    this.labelText.on('mousedown', (options) => {
      // if a user clicks the label it should enter editing mode
      this.measurementText.fire('mousedown', options);
      this.measurementText.selectAll();
    });

    this.measurementText.on('editing:entered', () => {
      this.measurementText.hiddenTextarea?.setAttribute(
        'maxlength',
        MEASUREMENT_LABEL_MAX_LENGTH.toString()
      );
      this.measurementText.selectAll();
      this.measurementText.updateFromTextArea();
    });

    this.measurementText.onKeyDown = (e: KeyboardEvent) => {
      if (e.key === KEYS.ENTER || e.key === KEYS.TAB) {
        this.measurementText.exitEditing();
      }
    };
  }

  public static newLabelText = (
    label: string | undefined,
    options?: Partial<TextOptions>
  ): fabric.Text =>
    new fabric.Text(label ?? '', {
      stroke: stroke.normal,
      fill: stroke.normal,
      strokeWidth: 0.1,
      fontSize: 3,
      fontFamily,
      name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL}_${uniqueId()}`,
      hoverCursor: 'pointer',
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      selectable: false,
      data: {
        highlightable: true,
        inInterior: true,
      },
      ...options,
    });

  public static newMeasureText = (
    label: string | undefined,
    interiorColor: string | fabric.Pattern | fabric.Gradient | undefined,
    options?: Partial<TextOptions>
  ): fabric.IText =>
    new fabric.IText(label ?? '', {
      fontFamily,
      stroke: stroke.normal,
      fill: interiorColor,
      strokeWidth: 0.1,
      editable: true,
      fontSize: 3,
      selectable: true,
      hoverCursor: 'pointer',
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL}_${uniqueId()}`,
      data: {
        highlightable: true,
        inInterior: true,
      },
      ...options,
    });

  toJSON() {
    return {
      parentShape: this.parentShape.id,
      labelText: this.labelText.text,
      measurementText: this.measurementText.text,
    };
  }

  hide(): void {
    this.measurementText.canvas?.remove(this.measurementText);
    this.labelText.canvas?.remove(this.labelText);
  }

  darken(): void {
    this.labelText.set({
      stroke: stroke.active,
      fill: stroke.active,
    });

    this.measurementText.set({
      stroke: stroke.active,
      fill: stroke.active,
    });
  }

  lighten(): void {
    this.measurementText.set({
      stroke: stroke.normal,
      fill: stroke.normal,
    });

    this.labelText.set({
      stroke: stroke.normal,
      fill: stroke.normal,
    });
  }
}
